import Service from '@/service/api'
import { getWorkspaceNavigation } from '@/utils/role-dashboard'
import {
  getRoleOrder,
  inferRolesFromPath,
  normalizePath,
  resolveRolesFromValue
} from '@/config/workspace'

export function getPayload (response) {
  return (response && response.data && response.data.data) || null
}

export function normalizeMultiLanguage (value) {
  if (Array.isArray(value)) return value
  if (!value) return []
  return [{ key: 'th', value: String(value) }]
}

export function getTextByLanguage (multiLanguage, language = 'th') {
  const items = normalizeMultiLanguage(multiLanguage)
  const byLanguage = items.find(item => item && item.key === language && item.value)
  if (byLanguage) return byLanguage.value
  const byEnglish = items.find(item => item && item.key === 'en' && item.value)
  if (byEnglish) return byEnglish.value
  const first = items.find(item => item && item.value)
  return first ? first.value : ''
}

export function toMultiLanguage (value, language = 'th') {
  const text = String(value || '').trim()
  if (!text) return []
  return [{ key: language, value: text }]
}

export function mapType (item) {
  return {
    _id: item && item._id ? item._id : '',
    title: normalizeMultiLanguage(item && item.title),
    description: normalizeMultiLanguage(item && item.description),
    name: getTextByLanguage(item && item.title),
    state: item && typeof item.state === 'boolean' ? item.state : true,
    stateLabel: item && item.state === false ? 'Inactive' : 'Active'
  }
}

export function mapMenu (item) {
  const typeObj = item && item.type && typeof item.type === 'object' ? item.type : null
  return {
    _id: item && item._id ? item._id : '',
    title: normalizeMultiLanguage(item && item.title),
    description: normalizeMultiLanguage(item && item.description),
    name: getTextByLanguage(item && item.title),
    descriptionText: getTextByLanguage(item && item.description),
    path: normalizePath(item && item.path ? item.path : ''),
    responsibleRoles: Array.isArray(item && item.responsibleRoles)
      ? item.responsibleRoles.map(value => String(value || '').trim().toLowerCase()).filter(Boolean)
      : [],
    showInNavigation: !(item && item.showInNavigation === false),
    navPath: normalizePath(item && item.navPath ? item.navPath : (item && item.path ? item.path : '')),
    navIcon: item && item.navIcon ? item.navIcon : '',
    navGroup: item && item.navGroup ? String(item.navGroup).trim() : '',
    navGroupIcon: item && item.navGroupIcon ? String(item.navGroupIcon).trim() : '',
    navOrder: Number(item && item.navOrder) || 0,
    navBadgeKey: item && item.navBadgeKey ? String(item.navBadgeKey).trim() : '',
    typeId: typeObj ? typeObj._id : (item && item.type ? item.type : ''),
    typeName: typeObj ? getTextByLanguage(typeObj.title) : '',
    source: item && item.source ? item.source : 'manual',
    state: item && typeof item.state === 'boolean' ? item.state : true,
    stateLabel: item && item.state === false ? 'Inactive' : 'Active'
  }
}

export function mapGroup (item) {
  const typeObj = item && item.visibleType && typeof item.visibleType === 'object' ? item.visibleType : null
  return {
    _id: item && item._id ? item._id : '',
    title: normalizeMultiLanguage(item && item.title),
    description: normalizeMultiLanguage(item && item.description),
    name: getTextByLanguage(item && item.title),
    descriptionText: getTextByLanguage(item && item.description),
    visibleTypeId: typeObj ? typeObj._id : (item && item.visibleType ? item.visibleType : ''),
    visibleTypeName: typeObj ? getTextByLanguage(typeObj.title) : '',
    state: item && typeof item.state === 'boolean' ? item.state : true,
    stateLabel: item && item.state === false ? 'Inactive' : 'Active'
  }
}

export function mapPermission (item) {
  const groupObj = item && item.group && typeof item.group === 'object' ? item.group : null
  const menuObj = item && item.menu && typeof item.menu === 'object' ? item.menu : null
  return {
    _id: item && item._id ? item._id : '',
    groupId: groupObj ? groupObj._id : (item && item.group ? item.group : ''),
    menuId: menuObj ? menuObj._id : (item && item.menu ? item.menu : ''),
    all: !!(item && item.all),
    view: !!(item && item.view),
    edit: !!(item && item.edit),
    delete: !!(item && item.delete),
    action: !!(item && item.action),
    owner: !!(item && item.owner),
    logs: !!(item && item.logs)
  }
}

export function inferGroupRoles (group) {
  return resolveRolesFromValue(group, [])
}

export function inferMenuResponsibleRoles (menu) {
  if (Array.isArray(menu && menu.responsibleRoles) && menu.responsibleRoles.length) {
    return menu.responsibleRoles
  }
  const path = normalizePath(menu && menu.path ? menu.path : '')
  if (path) {
    return inferRolesFromPath(path, getRoleOrder())
  }
  return resolveRolesFromValue(menu, getRoleOrder())
}

function collectNavigationEntries(items, target = []) {
  ;(items || []).forEach((item) => {
    if (!item || typeof item !== 'object') return
    const label = String(item.name || '').trim()
    const path = normalizePath(item.to || item.route || '')
    if (label && path) {
      target.push({
        title: label,
        description: `${label} route`,
        path
      })
    }
    if (Array.isArray(item.items)) collectNavigationEntries(item.items, target)
    if (Array.isArray(item._children)) collectNavigationEntries(item._children, target)
  })
  return target
}

function getMappedMenuSeeds() {
  const roles = getRoleOrder()
  const navigation = getWorkspaceNavigation('multiuser', roles, {})
  const entries = collectNavigationEntries(navigation)
  const seen = new Set()
  return entries.filter((item) => {
    if (!item.path || seen.has(item.path)) return false
    seen.add(item.path)
    return true
  })
}

async function ensureSecurityCatalog() {
  let types = (getPayload(await Service.security('types')) || []).map(mapType)
  if (!types.length) {
    await Service.security('create-type', {
      title: [
        { key: 'en', value: 'General Access' },
        { key: 'th', value: 'General Access' }
      ],
      state: true
    })
    types = (getPayload(await Service.security('types')) || []).map(mapType)
  }

  let menus = (getPayload(await Service.security('menus')) || []).map(mapMenu)
  if (!menus.length && types.length) {
    const defaultTypeId = types[0]._id
    const seeds = getMappedMenuSeeds()
    for (const item of seeds) {
      try {
        await Service.security('create-menu', {
          title: [
            { key: 'en', value: item.title },
            { key: 'th', value: item.title }
          ],
          description: [
            { key: 'en', value: item.description },
            { key: 'th', value: item.description }
          ],
          path: item.path,
          responsibleRoles: inferRolesFromPath(item.path, getRoleOrder()),
          showInNavigation: true,
          navPath: item.path,
          navIcon: '',
          navGroup: '',
          navGroupIcon: '',
          navOrder: 0,
          navBadgeKey: '',
          type: defaultTypeId,
          state: true
        })
      } catch (error) {
        // Ignore duplicates or partial seed collisions and continue.
      }
    }
    menus = (getPayload(await Service.security('menus')) || []).map((item) => {
      const mapped = mapMenu(item)
      return {
        ...mapped,
        source: getMappedMenuSeeds().some(seed => seed.path === mapped.path) ? 'mapped' : mapped.source
      }
    })
  } else {
    const seeds = getMappedMenuSeeds()
    menus = menus.map((item) => ({
      ...item,
      source: seeds.some(seed => seed.path === item.path) ? 'mapped' : item.source
    }))
  }

  return { types, menus }
}

export async function fetchSecurityBootstrap () {
  try {
    const bootstrapRes = await Service.security('bootstrap')
    const bootstrapPayload = getPayload(bootstrapRes) || {}
    if (
      Array.isArray(bootstrapPayload.types) &&
      Array.isArray(bootstrapPayload.menus) &&
      bootstrapPayload.types.length &&
      bootstrapPayload.menus.length
    ) {
      return {
        types: bootstrapPayload.types.map(mapType),
        menus: bootstrapPayload.menus.map(mapMenu),
        groups: (bootstrapPayload.groups || []).map(mapGroup),
        permissions: (bootstrapPayload.permissions || []).map(mapPermission)
      }
    }
  } catch (error) {
    // Older servers do not expose bootstrap yet; keep the client-side fallback.
  }

  const [{ types, menus }, groupsRes, permissionsRes] = await Promise.all([
    ensureSecurityCatalog(),
    Service.security('groups'),
    Service.security('permissions')
  ])

  return {
    types,
    menus,
    groups: (getPayload(groupsRes) || []).map(mapGroup),
    permissions: (getPayload(permissionsRes) || []).map(mapPermission)
  }
}
